/**
 * Preset values for the search range pickers (home hero «الفلاتر» panel and the
 * /search sidebar). Every picker also accepts a typed value, so a preset list is
 * a shortcut, never a limit. Values are plain numbers; the UI formats them with
 * utils/numberFormat (Western digits, "1,250,000").
 *
 * Rent presets come in two periods, following the شهري / سنوي toggle on the price
 * column. The chosen period travels to the API as `pricePeriod`, and the backend
 * compares each rent listing's price converted from the period its owner stated
 * (backend/server/lib/search/rentPeriod.js).
 *
 * OPEN-ENDED TOP. The last value of every list reads «N+» («5+», «500,000+»): in a
 * «من» list it simply means N or more; in a «إلى» list it means "no upper limit",
 * so the maximum is dropped when the search is built (dropOpenEndedMaxes).
 */
import { getPropertyCategory } from './propertyTypeConfig';
import type { SearchRangeKey } from './searchFilterFields';
import { toLatinDigits } from '../utils/arabicNormalize';

/** Listing scope as /search and the hero use it ('buy' = for sale). */
export type SearchListingType = 'rent' | 'buy' | 'all';

/** The period rent amounts are typed and compared in (the شهري / سنوي toggle). */
export type RentPricePeriod = 'monthly' | 'yearly';

/** Yearly is the Saudi norm for long-term rent, so it is the default. */
export const DEFAULT_RENT_PRICE_PERIOD: RentPricePeriod = 'yearly';

/** Bedrooms «من / إلى» choices (same 1–5 the filters have always offered). */
export const BEDROOM_PRESETS: readonly number[] = [1, 2, 3, 4, 5];

/** Bathrooms «من / إلى» choices (1–5, the last one open-ended: «5+»). */
export const BATHROOM_PRESETS: readonly number[] = [1, 2, 3, 4, 5];

export const PRICE_PRESETS: {
  readonly rent: readonly number[];
  readonly rentMonthly: readonly number[];
  readonly sale: readonly number[];
} = {
  /** Monthly rent, SAR. */
  rentMonthly: [
    1_000, 1_500, 2_000, 2_500, 3_000, 4_000, 5_000, 6_000, 8_000, 10_000, 12_500,
    15_000, 20_000, 30_000, 50_000,
  ],
  /** Yearly rent, SAR. */
  rent: [
    10_000, 15_000, 20_000, 25_000, 30_000, 40_000, 50_000, 60_000, 80_000,
    100_000, 150_000, 200_000, 300_000, 500_000,
  ],
  /** Sale price, SAR. */
  sale: [
    250_000, 500_000, 750_000, 1_000_000, 1_250_000, 1_500_000, 2_000_000, 2_500_000,
    3_000_000, 4_000_000, 5_000_000, 7_500_000, 10_000_000, 15_000_000, 20_000_000,
  ],
};

export const AREA_PRESETS: { readonly default: readonly number[]; readonly land: readonly number[] } = {
  /** Built space in m² (apartments up to warehouses). */
  default: [50, 75, 100, 125, 150, 200, 250, 300, 400, 500, 750, 1_000, 2_000, 5_000],
  /** Plot size in m² (land types and farms). */
  land: [
    300, 400, 500, 600, 750, 900, 1_000, 1_500, 2_000, 3_000, 5_000, 10_000, 20_000,
    50_000, 100_000,
  ],
};

const NO_PRESETS: readonly number[] = [];

/**
 * Price presets for a listing scope (rent in the chosen period). 'all' mixes rents
 * with sale prices, so it gets none — the field becomes typing only.
 */
export function getPricePresets(
  listingType: SearchListingType,
  pricePeriod: RentPricePeriod = DEFAULT_RENT_PRICE_PERIOD,
): readonly number[] {
  if (listingType === 'rent') return pricePeriod === 'monthly' ? PRICE_PRESETS.rentMonthly : PRICE_PRESETS.rent;
  if (listingType === 'buy') return PRICE_PRESETS.sale;
  return NO_PRESETS;
}

/** Area presets: plot sizes for land types and farms, built space otherwise. */
export function getAreaPresets(propertyType?: string | null): readonly number[] {
  const id = (propertyType || '').trim();
  if (id && getPropertyCategory(id.toUpperCase()) === 'land') return AREA_PRESETS.land;
  return AREA_PRESETS.default;
}

/** What decides each range's preset list. */
export interface RangePresetContext {
  listingType: SearchListingType;
  propertyType?: string | null;
  pricePeriod?: RentPricePeriod | null;
}

/** The preset list one range field offers. */
export function getRangePresets(key: SearchRangeKey, ctx: RangePresetContext): readonly number[] {
  switch (key) {
    case 'bedrooms':
      return BEDROOM_PRESETS;
    case 'bathrooms':
      return BATHROOM_PRESETS;
    case 'price':
      return getPricePresets(ctx.listingType, ctx.pricePeriod ?? DEFAULT_RENT_PRICE_PERIOD);
    default:
      return getAreaPresets(ctx.propertyType);
  }
}

/** The open-ended top value of a field's list («5+»), or null when it has no presets. */
export function openEndedTop(key: SearchRangeKey, ctx: RangePresetContext): number | null {
  const presets = getRangePresets(key, ctx);
  return presets.length ? presets[presets.length - 1] : null;
}

/** True when `max` is the field's «N+» choice, i.e. "no upper limit". */
export function isOpenEndedMax(key: SearchRangeKey, max: string | number | null | undefined, ctx: RangePresetContext): boolean {
  const top = openEndedTop(key, ctx);
  return top !== null && toNumberOrNaN(max) === top;
}

/** Label for a preset value: the top one reads «N+». */
export function presetLabel(value: number, presets: readonly number[], format: (n: number) => string): string {
  const text = format(value);
  return presets.length && value === presets[presets.length - 1] ? `${text}+` : text;
}

/** The flat filter names (URL / request) of each range's maximum. */
const MAX_KEYS = {
  bedrooms: 'bedroomsMax',
  bathrooms: 'bathroomsMax',
  price: 'maxPrice',
  area: 'maxArea',
} as const satisfies Record<SearchRangeKey, string>;

/**
 * Drops every maximum that is its list's «N+» choice: «إلى 5+» means "5 or more",
 * which is no upper limit at all. Call it where a URL or API request is built,
 * never on the form state (the field must keep showing «5+»). Returns the same
 * object when nothing changes.
 */
export function dropOpenEndedMaxes<
  T extends {
    listingType?: string | null;
    tab?: string | null;
    propertyType?: string | null;
    pricePeriod?: RentPricePeriod | null;
    bedroomsMax?: string;
    bathroomsMax?: string;
    maxPrice?: string;
    maxArea?: string;
  },
>(filters: T): T {
  const scope = filters.listingType ?? filters.tab ?? 'all';
  const ctx: RangePresetContext = {
    listingType: scope === 'rent' || scope === 'buy' ? scope : 'all',
    propertyType: filters.propertyType,
    pricePeriod: filters.pricePeriod,
  };
  let next: T = filters;
  (Object.keys(MAX_KEYS) as SearchRangeKey[]).forEach((key) => {
    const name = MAX_KEYS[key];
    const value = filters[name];
    if (value && isOpenEndedMax(key, value, ctx)) {
      if (next === filters) next = { ...filters };
      next[name] = '' as T[typeof name];
    }
  });
  return next;
}

/**
 * The presets a «إلى» (max) list should offer once a «من» (min) is chosen:
 * values above the min. `orEqual` keeps the min itself, which suits counts
 * (bedrooms 3 → 3 means "exactly 3"). No usable min → the full list.
 */
export function presetsAbove(
  presets: readonly number[],
  min: string | number | null | undefined,
  { orEqual = false }: { orEqual?: boolean } = {},
): readonly number[] {
  const n = toNumberOrNaN(min);
  if (!Number.isFinite(n)) return presets;
  return presets.filter((v) => (orEqual ? v >= n : v > n));
}

/** "250000", "250,000", "٢٥٠٠٠٠" or 250000 → 250000; blank or junk → NaN. */
function toNumberOrNaN(value: string | number | null | undefined): number {
  if (typeof value === 'number') return value;
  const s = toLatinDigits(String(value ?? '')).replace(/[,\s]/g, '');
  return s ? Number(s) : Number.NaN;
}
